import type { GameCatalogEntry, SessionSummary } from '@perplexicon/api-client';

import { resolveGameCatalogArt } from '../assets/asset-manifest.js';
import { GAME_CATALOG_ART, GAME_CATALOG_TAGS } from '../games/registry.js';
import { gameMetaChips } from '../lib/game-meta.js';
import { ActionLink } from './ActionLink.js';
import { Button } from './Button.js';
import { Card } from './Card.js';
import { StatusChip } from './StatusChip.js';
import type { StatusChipTone } from './StatusChip.js';

/**
 * Generic, metadata-driven predicate (CLAUDE.md §2 — no `gameId` branch)
 * for whether a game's catalog card should offer a "host a game" lobby
 * affordance instead of (or alongside) a two-player "play vs friend"
 * affordance: a non-solo mode whose maximum player count exceeds 2
 * (docs/games/word-rush/GAME_PLAN.md §13.9).
 */
export function canHostLobby(
  game: Pick<GameCatalogEntry, 'supportedModes' | 'playerCounts'>,
): boolean {
  return game.supportedModes.some((mode) => {
    if (mode === 'solo') return false;
    const counts = game.playerCounts[mode];
    return !!counts && counts.max > 2;
  });
}

/**
 * Generic, metadata-driven predicate for whether a game's catalog card
 * should offer the single-opponent "Play vs Friend" invite flow
 * (`createMultiplayerSession`, APP_SPEC §19-§20): a non-solo mode whose
 * declared range could be exactly two players. Explicitly excludes any
 * game declaring `joinModel: 'open_lobby'` (docs/games/word-rush/GAME_PLAN.md
 * §13.2) — for such a game, `POST /api/game-sessions` with an `opponent`
 * field always routes to `createLobbySession` instead, which silently
 * ignores it, so offering this affordance would be a dead end even when
 * the game's player-count range happens to include 2 (e.g. Word Rush's
 * `round_multiplayer: { min: 1, max: 10 }`). That game's own dedicated
 * lobby flow (`canHostLobby` above) is the correct multiplayer affordance
 * instead.
 */
export function canPlayVersus(
  game: Pick<GameCatalogEntry, 'supportedModes' | 'playerCounts' | 'joinModel'>,
): boolean {
  if (game.joinModel === 'open_lobby') return false;
  return game.supportedModes.some((mode) => {
    if (mode === 'solo') return false;
    const counts = game.playerCounts[mode];
    return !!counts && counts.min <= 2 && counts.max >= 2;
  });
}

export interface GameCardProps {
  game: GameCatalogEntry;
  onPlaySolo: () => void;
  isStarting: boolean;
  /** Opens the create-multiplayer-game flow (APP_SPEC §19-§20). Omitted where the caller doesn't offer it, or the game supports no invitable two-player mode. */
  onPlayVersus?: () => void;
  /** Opens the create-lobby flow (docs/games/word-rush/GAME_PLAN.md §11, §13.9, W1). Omitted where the caller doesn't offer it, or the game does not qualify per `canHostLobby`. */
  onHostLobby?: () => void;
  /**
   * The viewer's own in-progress session for this game, when they have one
   * (`activeSessionsByGame`, `lib/game-meta.ts`). Drives the "Active" status
   * chip and the "Continue Game" action; `null`/absent means they have none.
   * Never a hardcoded flag — it is real session state (APP_SPEC §18).
   */
  activeSession?: SessionSummary | null;
  /**
   * Heading level for the game's name, so the card keeps the surrounding
   * page's heading order (docs/UI_GUIDELINES.md §9): `h2` on the catalog,
   * where cards sit directly under the page title, `h3` on the dashboard,
   * where they sit under a section heading.
   */
  headingLevel?: 2 | 3;
}

/**
 * A game's own operational status (APP_SPEC §17, §48), shown only when it is
 * something the player should know about. `active` deliberately has no chip:
 * on this card "Active" means *the viewer has a session in progress*, and two
 * different meanings behind one word would be worse than saying nothing about
 * the ordinary case.
 */
export const GAME_STATUS_CHIPS: Partial<
  Record<GameCatalogEntry['status'], { label: string; tone: StatusChipTone }>
> = {
  beta: { label: 'Beta', tone: 'info' },
  maintenance: { label: 'Maintenance', tone: 'warning' },
  disabled: { label: 'Unavailable', tone: 'neutral' },
};

export interface GameCardState {
  /** Whether the card should offer "Play Solo" at all. */
  canStartSolo: boolean;
  /** Whether the card should offer "Play vs Friend". */
  showPlayVersus: boolean;
  /** Whether the card should offer "Host Game". */
  showHostLobby: boolean;
  /** Whether any launch action is offered — the card has *something* to start. */
  hasLaunchAction: boolean;
  /** Whether the game's own status (`active`/`beta`) allows starting it at all. */
  isVisible: boolean;
  /** The status chip to show for a non-`active` game, if any. */
  statusChip: { label: string; tone: StatusChipTone } | undefined;
}

/**
 * Whether/what a game card can offer to start it, derived once from the
 * game's own metadata and which launch callbacks the caller supplied —
 * `GameCard` and `GameCatalogCard` each independently re-derived this exact
 * seven-line block (`canStartSolo`, `isVisible`, `hasLaunchAction`, …) from
 * the same inputs before this existed.
 */
export function deriveGameCardState(
  game: Pick<GameCatalogEntry, 'supportedModes' | 'playerCounts' | 'joinModel' | 'status'>,
  hasPlayVersus: boolean,
  hasHostLobby: boolean,
): GameCardState {
  const supportsSolo = game.supportedModes.includes('solo');
  const isVisible = game.status === 'active' || game.status === 'beta';
  const canStartSolo = supportsSolo && isVisible;
  const showPlayVersus = hasPlayVersus && isVisible && canPlayVersus(game);
  const showHostLobby = hasHostLobby && isVisible && canHostLobby(game);
  const hasLaunchAction = canStartSolo || showPlayVersus || showHostLobby;
  const statusChip = GAME_STATUS_CHIPS[game.status];
  return { canStartSolo, showPlayVersus, showHostLobby, hasLaunchAction, isVisible, statusChip };
}

/**
 * A game catalog entry (TECH_STACK §16, APP_SPEC §17) — the one card the
 * catalog page and the dashboard both render, so a game is never described
 * two ways. Everything on it comes from the game's own registered metadata
 * plus the platform's session list; no game-specific branching exists here
 * (CLAUDE.md §2).
 *
 * Actions follow one rank: every way of *starting* this game is the same
 * decorated brass control, because none of them is more primary than another
 * (docs/UI_GUIDELINES.md §8). The single exception is a game already in
 * progress — then "Continue Game" is the primary action and the ways to start
 * another drop to the quiet secondary treatment beneath it.
 */
export function GameCard({
  game,
  onPlaySolo,
  isStarting,
  onPlayVersus,
  onHostLobby,
  activeSession,
  headingLevel = 2,
}: GameCardProps) {
  const { canStartSolo, showPlayVersus, showHostLobby, hasLaunchAction, isVisible, statusChip } =
    deriveGameCardState(game, !!onPlayVersus, !!onHostLobby);
  const Heading = headingLevel === 3 ? 'h3' : 'h2';
  const meta = [GAME_CATALOG_TAGS[game.gameId], ...gameMetaChips(game)].filter(Boolean);
  const art = resolveGameCatalogArt(GAME_CATALOG_ART[game.gameId]);

  // Once a session is in progress, starting another is a deliberate second
  // choice rather than the obvious action, so it is labelled as one.
  const launchButtons = (
    <div className="flex flex-wrap gap-2">
      {canStartSolo && (
        <Button
          variant={activeSession ? 'secondary' : 'primary'}
          emphasis={activeSession ? 'flat' : 'decorated'}
          size="sm"
          onClick={onPlaySolo}
          isLoading={isStarting}
        >
          {activeSession ? 'Solo' : 'Play Solo'}
        </Button>
      )}
      {showPlayVersus && (
        <Button
          variant={activeSession ? 'secondary' : 'primary'}
          emphasis={activeSession ? 'flat' : 'decorated'}
          size="sm"
          onClick={onPlayVersus}
        >
          {activeSession ? 'Vs Friend' : 'Play vs Friend'}
        </Button>
      )}
      {showHostLobby && (
        <Button
          variant={activeSession ? 'secondary' : 'primary'}
          emphasis={activeSession ? 'flat' : 'decorated'}
          size="sm"
          onClick={onHostLobby}
        >
          {activeSession ? 'Host' : 'Host Game'}
        </Button>
      )}
    </div>
  );

  return (
    <Card
      data-testid={`game-card-${game.gameId}`}
      className="interactive-lift flex h-full flex-col gap-0 overflow-hidden p-0 hover:shadow-3"
    >
      <div className="aspect-video overflow-hidden border-b border-border bg-frame">
        <img
          src={art.src}
          srcSet={art.srcSet}
          // The card is roughly a third of the content column on wide
          // screens, half of it on tablets, and the full width on mobile, so
          // the browser can pick the 640 file for most placements and the
          // 1280 only where it genuinely helps (docs/UI_GUIDELINES.md §12).
          sizes="(min-width: 1280px) 22rem, (min-width: 640px) 45vw, 100vw"
          width={640}
          height={360}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3 tablet:p-4">
        <div className="flex items-start justify-between gap-2">
          <Heading className="font-display text-base font-semibold leading-snug text-textPrimary tablet:text-lg">
            {game.name}
          </Heading>
          <div className="flex shrink-0 flex-wrap justify-end gap-1">
            {activeSession && <StatusChip tone="active">Active</StatusChip>}
            {statusChip && <StatusChip tone={statusChip.tone}>{statusChip.label}</StatusChip>}
          </div>
        </div>
        {/*
          A list, not a run-on sentence: each fact is its own item, so a
          screen reader reads them as separate values rather than
          "DeductionSolo2 players". The separator is a border rather than a
          punctuation character, so nothing decorative reaches the
          accessibility tree either (docs/UI_GUIDELINES.md §9).
        */}
        {meta.length > 0 && (
          <ul
            aria-label={`${game.name} details`}
            className="flex flex-wrap items-center text-xs text-textMuted"
          >
            {meta.map((chip) => (
              <li key={chip} className="border-l border-border pl-2 first:border-l-0 first:pl-0">
                <span className="pr-2">{chip}</span>
              </li>
            ))}
          </ul>
        )}
        <p className="text-sm text-textSecondary">{game.description}</p>
        <div className="mt-auto flex flex-col gap-2 pt-1">
          {activeSession ? (
            <>
              <ActionLink
                to={`/play/${activeSession.id}`}
                variant="primary"
                emphasis="decorated"
                className="self-start"
              >
                Continue Game
                <span className="sr-only"> — {game.name}</span>
              </ActionLink>
              {hasLaunchAction && (
                <div className="flex flex-col gap-1">
                  <p className="text-xs text-textMuted">Play another:</p>
                  {launchButtons}
                </div>
              )}
            </>
          ) : hasLaunchAction ? (
            launchButtons
          ) : (
            <p className="text-sm text-textMuted">
              {isVisible ? 'No playable mode available right now.' : 'Currently unavailable.'}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
